<script setup>
import { computed } from 'vue';
import questionsData from '@/lib/GlobalInvestQuestions.json';

const props = defineProps({
  answers: { type: Object, required: true },
  disabled: { type: Boolean, default: false }
});

const emit = defineEmits(['edit', 'submit']);

const questions = questionsData.questions || [];

const prettyAnswers = computed(() => {
  const result = [];

  questions.forEach(q => {
    const answer = props.answers?.answers?.[q.id];
    let displayValue = 'Not provided';

    if (answer) {
      if (Array.isArray(answer)) {
        // Multi-select: show all selected option labels
        const selectedOptions = q.options
          .filter(opt => answer.includes(opt.id))
          .map(opt => opt.label);
        displayValue = selectedOptions.length > 0 ? selectedOptions.join(', ') : 'Not provided';
      } else {
        // Single-select: show the selected option label
        const selectedOption = q.options.find(opt => opt.id === answer);
        displayValue = selectedOption ? selectedOption.label : answer;
      }
    }

    result.push({
      label: q.text,
      value: displayValue
    });
  });

  return result;
});
</script>

<template>
  <div class="review-root">
    <h2 class="title">Review your answers</h2>
    <p class="subtitle">Check if these answers capture your investment preferences correctly before we generate recommendations.</p>

    <div class="review-grid">
      <div v-for="(row, idx) in prettyAnswers" :key="idx" class="review-row">
        <div class="q">{{ row.label }}</div>
        <div class="a">{{ row.value }}</div>
      </div>
    </div>

    <div class="actions">
      <button class="btn-secondary" type="button" @click="emit('edit')" :disabled="disabled">
        ← Change answers
      </button>
      <button class="btn-primary" type="button" @click="emit('submit')" :disabled="disabled">
        Get my recommendation
      </button>
    </div>
  </div>
</template>

<style scoped>
.review-root {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.title {
  margin: 0;
  font-size: 1.1rem;
}

.subtitle {
  margin: 0;
  font-size: 0.85rem;
  color: #868e96;
}

.review-grid {
  border-radius: 8px;
  border: 1px solid #e9ecef;
  padding: 12px 14px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 12px 16px;
}

.review-row {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.q {
  font-size: 0.8rem;
  color: #868e96;
  font-weight: 500;
}

.a {
  font-size: 0.9rem;
  font-weight: 600;
  color: #343a40;
}

.actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding-top: 8px;
}

.btn-primary,
.btn-secondary {
  border-radius: 999px;
  padding: 9px 18px;
  font-size: 0.9rem;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-primary {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: #fff;
}

.btn-primary:hover:not(:disabled) {
  background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(37, 99, 235, 0.3);
}

.btn-secondary {
  background: #f1f3f5;
  color: #343a40;
}

.btn-secondary:hover:not(:disabled) {
  background: #e9ecef;
}

.btn-primary:disabled,
.btn-secondary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

@media (max-width: 768px) {
  .review-grid {
    grid-template-columns: 1fr;
  }

  .actions {
    flex-direction: column-reverse;
  }

  .btn-primary,
  .btn-secondary {
    width: 100%;
  }
}
</style>
